/**
 * SignupProgressBar - FOMO Component
 *
 * A progress bar that fills up with each signup, creating urgency.
 * Uses HVME mediakit colors (Aurora Lilac palette) - 10X Zama, don't copy!
 */

import { motion, useSpring, useTransform } from "framer-motion";
import { useEffect, useState } from "react";

interface SignupProgressBarProps {
  /** Current number of signups */
  current: number;
  /** Maximum signups allowed (goal/limit) */
  total: number;
  /** Label to display */
  label?: string;
  /** Show percentage text */
  showPercentage?: boolean;
  /** Show count text (e.g., "847 / 1,000") */
  showCount?: boolean;
  /** Pulsing glow when above threshold */
  urgencyThreshold?: number;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Custom class name */
  className?: string;
}

export function SignupProgressBar({
  current,
  total,
  label = "Genesis Node Allocation",
  showPercentage = true,
  showCount = true,
  urgencyThreshold = 80,
  size = "md",
  className = "",
}: SignupProgressBarProps) {
  const percentage = Math.min((current / total) * 100, 100);
  const isUrgent = percentage >= urgencyThreshold;
  const spotsRemaining = Math.max(total - current, 0);

  // Animated progress value
  const springProgress = useSpring(0, { stiffness: 50, damping: 20 });
  const displayPercentage = useTransform(springProgress, (v) => Math.round(v));

  useEffect(() => {
    springProgress.set(percentage);
  }, [percentage, springProgress]);

  // Size variants
  const sizeClasses = {
    sm: "h-2",
    md: "h-3",
    lg: "h-4",
  };

  const textSizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Header Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`font-medium text-hvme-text-primary ${textSizeClasses[size]}`}>
            {label}
          </span>
          {isUrgent && (
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="px-2 py-0.5 text-xs font-bold rounded-full bg-hvme-aurora-lilac/20 text-hvme-aurora-lilac border border-hvme-aurora-lilac/30 animate-pulse-urgency"
            >
              {spotsRemaining <= 10 ? "ALMOST FULL!" : "FILLING FAST!"}
            </motion.span>
          )}
        </div>

        {showCount && (
          <motion.span
            key={current}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`font-mono ${textSizeClasses[size]} text-hvme-text-secondary`}
          >
            <span className="text-hvme-aurora-lilac font-bold">{current.toLocaleString()}</span>
            <span className="text-hvme-text-muted"> / {total.toLocaleString()}</span>
          </motion.span>
        )}
      </div>

      {/* Progress Bar Container */}
      <div
        className={`relative w-full ${sizeClasses[size]} rounded-full overflow-hidden bg-hvme-bg-surface border border-hvme-border`}
      >
        {/* Animated Fill */}
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${percentage}%`,
            background: isUrgent
              ? "linear-gradient(90deg, #E6BEFA, #DCA0FA, #E6BEFA)"
              : "linear-gradient(90deg, #758eff, #E6BEFA)",
            backgroundSize: "200% 100%",
          }}
          initial={{ width: 0 }}
          animate={{
            width: `${percentage}%`,
            backgroundPosition: isUrgent ? ["0% 50%", "100% 50%"] : "0% 50%",
          }}
          transition={{
            width: { duration: 1, ease: "easeOut" },
            backgroundPosition: isUrgent
              ? { duration: 2, repeat: Infinity, ease: "linear" }
              : { duration: 0 },
          }}
        />

        {/* Shimmer Effect */}
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
            backgroundSize: "50% 100%",
          }}
          animate={{
            backgroundPosition: ["-100% 0%", "200% 0%"],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Glow Effect for Urgent State */}
        {isUrgent && (
          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{
              boxShadow: [
                "0 0 10px rgba(230, 190, 250, 0.3)",
                "0 0 25px rgba(230, 190, 250, 0.6)",
                "0 0 10px rgba(230, 190, 250, 0.3)",
              ],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        )}
      </div>

      {/* Bottom Row */}
      <div className="flex items-center justify-between mt-2">
        {showPercentage && (
          <motion.span className={`font-mono ${textSizeClasses[size]} text-hvme-text-muted`}>
            <motion.span className="text-hvme-aurora-lilac">
              {displayPercentage}
            </motion.span>
            % filled
          </motion.span>
        )}

        {spotsRemaining > 0 && spotsRemaining <= 50 && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`font-semibold ${textSizeClasses[size]} ${
              spotsRemaining <= 10
                ? "text-hvme-aurora-lilac animate-pulse"
                : "text-hvme-soft-plasma"
            }`}
          >
            Only {spotsRemaining} spots left!
          </motion.span>
        )}
      </div>
    </div>
  );
}

/**
 * Compact inline variant for nav/header
 */
export function SignupProgressBarCompact({
  current,
  total,
  className = "",
}: Pick<SignupProgressBarProps, "current" | "total" | "className">) {
  const percentage = Math.min((current / total) * 100, 100);
  const isAlmostFull = percentage >= 90;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="relative w-20 h-1.5 rounded-full overflow-hidden bg-hvme-bg-surface border border-hvme-border/50">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-hvme-primary to-hvme-aurora-lilac"
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
      <span className="text-xs font-mono text-hvme-text-muted">
        {isAlmostFull ? (
          <span className="text-hvme-aurora-lilac animate-pulse">{Math.round(percentage)}%</span>
        ) : (
          `${Math.round(percentage)}%`
        )}
      </span>
    </div>
  );
}

export default SignupProgressBar;
